import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { AlderskontrollGuide } from "@/components/AlderskontrollGuide";
import { ALDERSKONTROLL_FAQS } from "@/components/alderskontroll-faqs";
import { faqJsonLd, articleJsonLd } from "@/lib/seo-jsonld";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { FREE_CTA, PREMIUM_CTA } from "@/lib/cta";
import { LAST_REVIEWED } from "@/lib/official-sources";

const PAGE_URL = "https://alkoholprovet.se/alderskontroll-servering";
const OG_IMAGE = "https://alkoholprovet.se/og/default.jpg";
const TITLE = "Ålderskontroll vid servering – 18-årsgränsen 2026";
const DESCRIPTION =
  "Guide till ålderskontroll vid alkoholservering: 18-årsgränsen, 25-årsregeln, godkända id-handlingar, langning, kontrollköp och sanktioner enligt alkohollagen.";

export const Route = createFileRoute("/alderskontroll-servering")({
  component: AlderskontrollPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      articleJsonLd({
        url: PAGE_URL,
        headline: TITLE,
        description: DESCRIPTION,
        dateModified: LAST_REVIEWED,
      }),
      faqJsonLd(ALDERSKONTROLL_FAQS),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Ålderskontroll vid servering", url: PAGE_URL },
      ]),
    ],
  }),
});

function AlderskontrollPage() {
  return (
    <div className="container-narrow py-12 sm:py-16">
      <Breadcrumbs
        items={[
          { label: "Hem", to: "/" },
          { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
          { label: "Ålderskontroll vid servering" },
        ]}
      />

      <span className="inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-xs font-medium text-accent">
        <ShieldCheck className="h-3.5 w-3.5" /> Uppdaterat för 2026
      </span>
      <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-balance">
        Ålderskontroll vid alkoholservering
      </h1>
      <p className="mt-4 text-lg text-muted-foreground text-balance">
        Så fungerar 18-årsgränsen i alkohollagen, varför många ställen kontrollerar upp till 25 år, vilka
        id-handlingar som godtas och vad som händer om ålderskontrollen brister vid tillsyn.
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link to={FREE_CTA.to} className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">
          {FREE_CTA.label} <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to={PREMIUM_CTA.to} className="inline-flex items-center gap-2 rounded-xl border border-accent/40 bg-accent/10 px-5 py-3 text-sm font-medium text-accent hover:bg-accent/15">
          {PREMIUM_CTA.label} <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to="/egenkontrollparm" className="inline-flex items-center gap-2 rounded-xl border border-border px-5 py-3 text-sm font-medium hover:bg-secondary">
          Egenkontrollpärm med färdig rutin <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <AlderskontrollGuide />
    </div>
  );
}
